import { useCallback, useEffect, useState } from 'react';

// localStorage can be unavailable or throw (Safari private mode, partitioned storage when
// the site is embedded in an iframe). The Map fallback degrades "remembered across visits"
// to "remembered for this page load", which never crashes and never nags twice in a session.
const sessionFallback = new Map<string, string>();

const seenKey = (clientId: string) => `curated-tour-seen:${(clientId ?? '').toUpperCase()}`;

function readFlag(key: string): boolean {
  try {
    if (window.localStorage.getItem(key) !== null) return true;
  } catch {
    // fall through to the session fallback
  }
  return sessionFallback.has(key);
}

function writeFlag(key: string): void {
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Only remember in-memory when localStorage is unavailable, so the durable store
    // stays the single source of truth whenever it works.
    sessionFallback.set(key, '1');
  }
}

// Joyride scrolls whichever ancestor scrolls each target: the window on mobile, and the
// card list's own overflow container on desktop. Walk up from the deepest target and put
// every scrolled container, plus the window, back at the top when the tour ends.
function resetScrollToTop(): void {
  const depositButton = document.querySelector('[data-tour="make-deposit-button"]');
  for (let el = depositButton?.parentElement ?? null; el; el = el.parentElement) {
    if (el.scrollTop > 0) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  if (window.scrollY > 0) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

export interface UseCuratedSiteTourOptions {
  clientId: string;
  enabled: boolean;
  ready: boolean;
}

export interface UseCuratedSiteTourResult {
  runTour: boolean;
  tourKey: number;
  startTour: () => void;
  handleTourFinish: () => void;
}

// Auto-starts the curated site tour once per browser (per client), remembers both
// completion and skip, and exposes startTour for the replay help button.
export function useCuratedSiteTour({ clientId, enabled, ready }: UseCuratedSiteTourOptions): UseCuratedSiteTourResult {
  const [runTour, setRunTour] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  useEffect(() => {
    if (!enabled || !ready || runTour || readFlag(seenKey(clientId))) {
      return;
    }
    // Let Kendo panels and cards finish layout after the data-gated render before
    // Joyride measures targets; GuidedTour's own missing-target filter is the backstop.
    const timer = window.setTimeout(() => setRunTour(true), 400);
    return () => window.clearTimeout(timer);
  }, [enabled, ready, runTour, clientId]);

  // Fires on finish AND on skip/close, so the page never stays parked mid-scroll.
  const handleTourFinish = useCallback(() => {
    setRunTour(false);
    writeFlag(seenKey(clientId));
    resetScrollToTop();
  }, [clientId]);

  // Replay ignores the seen flag; a fresh tourKey remounts Joyride so it restarts at step 1.
  const startTour = useCallback(() => {
    setTourKey((key) => key + 1);
    setRunTour(true);
  }, []);

  return { runTour, tourKey, startTour, handleTourFinish };
}
